import { Row, Col, Card, Button, message } from 'antd';
import { EditOutlined, DeleteOutlined, LogoutOutlined, UserOutlined, MailOutlined, CalendarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { Team } from '../types';
import '../styles/index.less';

interface TeamCardViewProps {
  teams: Team[];
  onEdit: (team: Team) => void;
  onDelete: (id: string) => void;
  onLeave: (id: string) => void;
  onCardClick: (team: Team) => void;
  canDeleteTeam: (team: Team) => boolean;
  canEditTeam: (team: Team) => boolean;
  canLeaveTeam: (team: Team) => boolean;
}

export const TeamCardView: React.FC<TeamCardViewProps> = ({
  teams,
  onEdit,
  onDelete,
  onLeave,
  onCardClick,
  canDeleteTeam,
  canEditTeam,
  canLeaveTeam,
}) => {
  const handleCopyEmail = (email: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(email).then(
      () => {
        message.success('คัดลอกอีเมลสำเร็จ');
      },
      () => {
        message.error('ไม่สามารถคัดลอกอีเมลได้');
      }
    );
  };

  return (
    <Row gutter={[16, 16]}>
      {teams.map((team) => {
        const leader = team.members.find((m) => m.role === 1);
        const canDelete = canDeleteTeam(team);
        const canEdit = canEditTeam(team);
        const canLeave = canLeaveTeam(team);

        return (
          <Col key={team.id} xs={24} sm={12} lg={8}>
            <Card className="team-card" bodyStyle={{ padding: 0 }}>
              {/* Header */}
              <div className="team-card-header">
                <h3>{team.name}</h3>
                <div className="team-card-actions">
                  {canEdit && (
                    <Button
                      size="small"
                      shape="circle"
                      icon={<EditOutlined />}
                      className="btn-edit"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(team);
                      }}
                    />
                  )}
                  {canDelete && (
                    <Button
                      size="small"
                      shape="circle"
                      icon={<DeleteOutlined />}
                      className="btn-delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(team.id);
                      }}
                    />
                  )}
                </div>
              </div>

              {/* Body - Clickable */}
              <div className="team-card-body" onClick={() => onCardClick(team)}>
                {/* Email Box */}
                <div className="email-box">
                  <p>หากต้องการเข้าร่วมทีมกรุณาติดต่อ Email:</p>
                  <p
                    className="email-text"
                    onClick={(e) => handleCopyEmail(leader?.email || '', e)}
                    title="คลิกเพื่อคัดลอกอีเมล"
                  >
                    <MailOutlined /> {leader?.email || 'N/A'}
                  </p>
                </div>

                {/* Members */}
                <div className="members-section">
                  <h4 className="members-header">
                    <UserOutlined /> Member:
                  </h4>
                  <div className="members-list">
                    {team.members.map((member) => (
                      <div key={member.id} className="member-item">
                        <span className="member-name">
                          <UserOutlined />
                          {member.name}
                        </span>
                        <span className={`member-role ${member.role === 1 ? 'leader' : 'member'}`}>
                          {member.role === 1 ? 'Leader' : 'Member'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="team-card-footer">
                <span className="update-date">
                  <CalendarOutlined /> Lasted update: {dayjs(team.createdAt).format('DD/MM/YYYY')}
                </span>
                <span className="member-count">{team.members.length} สมาชิก</span>
              </div>
            </Card>
          </Col>
        );
      })}
    </Row>
  );
};
